import { useCallback, useEffect, useMemo, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import { MapContainer, TileLayer, useMapEvents } from "react-leaflet";
import { Link, useSearchParams } from "react-router-dom";
import "leaflet/dist/leaflet.css";

import { aircraft as aircraftApi, ApiError, rules as rulesApi, type AlertRule } from "../api/client";
import AircraftLayer from "../components/AircraftLayer";
import ZoneLayer from "../components/ZoneLayer";
import DetailSheet from "../components/DetailSheet";
import AltitudeLegend from "../components/AltitudeLegend";
import HomePrompt from "../components/HomePrompt";
import SearchBox from "../components/SearchBox";
import SizeFilterChip, { loadSizeFilter, saveSizeFilter, type MapSizeFilter } from "../components/SizeFilterChip";
import ProjectionLayer from "../components/ProjectionLayer";
import RouteArcLayer from "../components/RouteArcLayer";
import TrailLayer from "../components/TrailLayer";
import { t } from "../i18n/en";
import { BASEMAP, BASEMAP_NEEDS_DARKENING } from "../map/basemap";
import { bboxContains, padBbox, type Bbox } from "../map/types";
import { useDelayedFlag, useLiveAircraft, useTicker } from "../map/useLiveAircraft";
import { useMutes } from "../map/useMutes";
import { useSelection } from "../map/useSelection";
import { useSettings } from "../settings/useSettings";

// Where the map starts before geolocation answers — or if it never does. Budapest, because that
// is where this is used; it is a first frame, not a setting.
const FALLBACK_CENTER: [number, number] = [47.4979, 19.0402];
const FALLBACK_ZOOM = 10;
// Callsign labels only when zoomed in and the view isn't crowded — a label per marker over a whole
// region is noise, and ~60 permanent tooltips is where a phone starts to feel it.
const LABEL_MIN_ZOOM = 11;
const LABEL_MAX_VISIBLE = 60;

/** Tapping bare map closes the aircraft panel — the gesture every map app has taught people.
 *  Marker clicks don't propagate to the map, and Leaflet suppresses the click that ends a drag,
 *  so panning and selecting are unaffected. */
/**
 * Move the map to a point: animated when that's pleasant, instant when it can't be.
 *
 * Leaflet's flyTo is driven by requestAnimationFrame, which browsers stop entirely in a hidden
 * page. Found while verifying search in a background browser tab: the fly never advanced, `moveend`
 * never fired, the stream never resubscribed, and "Finding…" timed out on an aircraft that was
 * tracked the whole time. A phone user who switches apps mid-fly hits the same thing. And people
 * who ask their OS for reduced motion should not get a zoom-out-zoom-in swoop.
 */
function moveMap(map: LeafletMap, center: [number, number], zoom: number) {
  const animate =
    document.visibilityState === "visible" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (animate) map.flyTo(center, zoom);
  else map.setView(center, zoom, { animate: false });
}

/** "06:17", or "Mon 06:17" when more than a day away (polar edge cases, long quiet hours). */
function formatWake(ms: number): string {
  const when = new Date(ms);
  const time = when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return ms - Date.now() > 24 * 3600_000 ? `${when.toLocaleDateString("en-GB", { weekday: "short" })} ${time}` : time;
}

function DeselectOnMapClick({ onDeselect }: { onDeselect: () => void }) {
  useMapEvents({ click: onDeselect });
  return null;
}

function ViewportWatcher({ onChange, onZoom }: { onChange: (bbox: Bbox) => void; onZoom: (zoom: number) => void }) {
  const report = (map: LeafletMap) => {
    const b = map.getBounds();
    onZoom(map.getZoom());
    onChange({
      minLat: b.getSouth(),
      minLon: b.getWest(),
      maxLat: b.getNorth(),
      maxLon: b.getEast(),
    });
  };
  const map = useMapEvents({
    moveend: () => report(map),
    zoomend: () => report(map),
  });
  // The first report: `moveend` doesn't fire for the initial view, so without this the stream
  // never opens until the user touches the map.
  useEffect(() => report(map), []);
  return null;
}

export default function Radar() {
  const [map, setMap] = useState<LeafletMap | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { settings, patch } = useSettings();
  const { muted, toggle: toggleMute } = useMutes();
  const [viewport, setViewport] = useState<Bbox | null>(null);
  const [subscription, setSubscription] = useState<Bbox | null>(null);
  const [selectedHex, setSelectedHex] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(false);
  // The device's own position, kept for the detail sheet's distance and bearing. This is not
  // the alert anchor — that is saved server-side and arrives with alerts in Phase 4.
  const [devicePosition, setDevicePosition] = useState<{ lat: number; lon: number } | null>(null);
  const [zoom, setZoom] = useState(FALLBACK_ZOOM);
  const [notice, setNotice] = useState<string | null>(null);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [sizeFilter, setSizeFilter] = useState<MapSizeFilter>(loadSizeFilter);
  const units = settings?.units ?? "aviation";

  // Rules drive the zone rings and the "asleep" note, both of which change with the time of day. Loaded
  // once per visit they went stale on a map left open: the note would never appear at sunset or clear
  // at sunrise. So: on open, when the app returns to the foreground, every 10 minutes, and at the
  // earliest wake time.
  const reloadRules = useCallback(() => {
    rulesApi.list().then(setRules).catch(() => undefined);
  }, []);
  useEffect(() => {
    reloadRules();
    const every = setInterval(reloadRules, 10 * 60_000);
    const onVisible = () => document.visibilityState === "visible" && reloadRules();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(every);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reloadRules]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  // Re-subscribe only when the viewport leaves the padded box we already asked for. Panning a
  // little is the most common gesture on a phone, and reopening the stream for each one would
  // churn the connection and re-plan the poller's circles several times a second.
  useEffect(() => {
    if (!viewport) return;
    if (!subscription || !bboxContains(subscription, viewport)) {
      setSubscription(padBbox(viewport));
    }
  }, [viewport, subscription]);

  // A 401 on the stream means the session expired; the app remounts at the login screen when
  // the next request confirms it, so reloading is the honest response here.
  const { aircraft, receivedAt, connected, loading } = useLiveAircraft(subscription, () => window.location.reload());

  // Markers keep fading between ticks rather than freezing until the next snapshot.
  // 1 s, paired with a 1 s CSS transition on the marker transform, reads as continuous motion.
  useTicker(1000);
  const ageOffsetS = (Date.now() - receivedAt) / 1000;
  const loadingShown = useDelayedFlag(loading);

  const inView = useMemo(
    () =>
      viewport
        ? aircraft.filter(
            (a) =>
              a.lat >= viewport.minLat &&
              a.lat <= viewport.maxLat &&
              a.lon >= viewport.minLon &&
              a.lon <= viewport.maxLon,
          )
        : aircraft,
    [aircraft, viewport],
  );

  // Flying to an aircraft that may be outside the streamed viewport — from search, or from a
  // notification. The map flies there, and the aircraft is selected once the stream delivers it:
  // selecting straight away would be undone at once by the "selection left the stream" cleanup.
  const [pendingFocus, setPendingFocus] = useState<{ hex: string; label: string } | null>(null);

  const focusOn = useCallback(
    (target: { hex: string; lat: number; lon: number; flight?: string; reg?: string }) => {
      setPendingFocus({ hex: target.hex, label: target.flight ?? target.reg ?? target.hex.toUpperCase() });
      if (map) moveMap(map, [target.lat, target.lon], Math.max(map.getZoom(), 10));
    },
    [map],
  );

  // Resolution: select as soon as the stream has it.
  useEffect(() => {
    if (pendingFocus && aircraft.some((a) => a.hex === pendingFocus.hex)) {
      setSelectedHex(pendingFocus.hex);
      setPendingFocus(null);
    }
  }, [pendingFocus, aircraft]);

  // Give-up: keyed on the pending target alone. The earlier version also depended on `aircraft`,
  // so every 5-second tick cleared and restarted the timer and it never fired — "Finding…" would
  // have spun forever for an aircraft that had left coverage.
  useEffect(() => {
    if (!pendingFocus) return;
    const giveUp = setTimeout(() => {
      setPendingFocus(null);
      setNotice(t.radar.notTracked(pendingFocus.label));
    }, 15_000);
    return () => clearTimeout(giveUp);
  }, [pendingFocus]);

  // Arriving from a notification: /?focus=<hex>. Asks the server rather than the stream — the
  // aircraft is usually not in the current viewport, which is exactly why the old version, which
  // only looked at the stream, often did nothing at all.
  const focusHex = searchParams.get("focus");
  useEffect(() => {
    if (!focusHex || !map) return;
    // Consumed once: leaving it in the URL would re-focus on every later render.
    searchParams.delete("focus");
    setSearchParams(searchParams, { replace: true });
    aircraftApi
      .detail(focusHex.toLowerCase())
      .then((a) => focusOn(a))
      .catch((error) => {
        if (error instanceof ApiError && error.status === 404) setNotice(t.radar.notTracked(focusHex.toUpperCase()));
      });
  }, [focusHex, map]);

  const showSpinner = Boolean(pendingFocus) || loadingShown;

  // Alerts asleep: at least one live rule, and every live rule sleeping. Earliest wake wins. Dormant
  // and disabled rules are excluded — they aren't asleep, they can't fire at all.
  const liveRules = rules.filter((r) => r.enabled && !r.dormant_reason);
  const earliestWake = liveRules
    .map((r) => (r.wakes_at ? new Date(r.wakes_at).getTime() : null))
    .filter((v): v is number => v !== null)
    .sort((a, b) => a - b)[0];
  const allAsleep =
    liveRules.length > 0 &&
    liveRules.every((r) => r.sleep_reason) &&
    // Hidden the moment the wake time passes, without waiting for the refetch.
    (earliestWake === undefined || earliestWake > Date.now());
  useEffect(() => {
    if (!allAsleep || earliestWake === undefined) return;
    const timer = setTimeout(reloadRules, Math.max(1000, earliestWake - Date.now() + 5000));
    return () => clearTimeout(timer);
  }, [allAsleep, earliestWake, reloadRules]);

  // The map size filter narrows only what is drawn. The selected aircraft and one being flown to
  // always stay visible — hiding them would leave an open panel with no marker, or a search jump
  // that lands on nothing.
  const visible = useMemo(
    () =>
      sizeFilter === 0
        ? inView
        : inView.filter((a) => (a.sz ?? 0) >= sizeFilter || a.hex === selectedHex || a.hex === pendingFocus?.hex),
    [inView, sizeFilter, selectedHex, pendingFocus],
  );

  const selected = selectedHex ? aircraft.find((a) => a.hex === selectedHex) : undefined;
  // `receivedAt` as the generation: the projection is anchored to the aircraft's current
  // position, so it is refetched whenever a new snapshot lands.
  const { detail, photo, route, outlook, track, projection, loading: projectionLoading } = useSelection(selectedHex, receivedAt);
  // An aircraft that leaves coverage is dropped from the store, and a sheet showing a husk is
  // worse than no sheet.
  useEffect(() => {
    if (selectedHex && !selected) setSelectedHex(null);
  }, [selectedHex, selected]);

  const locate = useCallback(() => {
    if (!map || !navigator.geolocation) return;
    setLocating(true);
    setLocationError(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setDevicePosition({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        moveMap(map, [pos.coords.latitude, pos.coords.longitude], Math.max(map.getZoom(), 10));
      },
      () => {
        setLocating(false);
        setLocationError(true);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  }, [map]);

  // Centre on the user once, on open. Deliberately not repeated: the map fighting you for
  // control while you pan is the classic way a map app becomes unusable.
  // Not when opened from a notification: the locate fly would race the focus fly, win, and leave the
  // map somewhere the aircraft isn't.
  useEffect(() => {
    if (map && !searchParams.get("focus")) locate();
  }, [map]);

  return (
    <div className="radar">
      <MapContainer
        ref={setMap}
        center={FALLBACK_CENTER}
        zoom={FALLBACK_ZOOM}
        zoomControl={false}
        maxZoom={BASEMAP.maxZoom}
        className={`map${BASEMAP_NEEDS_DARKENING ? " map-dark" : ""}`}
      >
        <TileLayer
          // Isolated in map/basemap.ts — see the note there on why this is not CARTO any more.
          url={BASEMAP.url}
          subdomains={BASEMAP.subdomains ?? "abc"}
          maxNativeZoom={BASEMAP.maxNativeZoom}
          maxZoom={BASEMAP.maxZoom}
          attribution={BASEMAP.attribution}
        />
        <ViewportWatcher onChange={setViewport} onZoom={setZoom} />
        <DeselectOnMapClick onDeselect={() => setSelectedHex(null)} />
        <ZoneLayer rules={rules} settings={settings} />
        {/* Under the trail and forecast, which are more specific; all under the markers. */}
        {selected && <RouteArcLayer route={route} aircraft={selected} zoom={zoom} />}
        {/* Under the markers: an aircraft must never be hidden by its own trail. */}
        {selected && <TrailLayer points={track} />}
        {selected && <ProjectionLayer points={projection} />}
        <AircraftLayer
          aircraft={visible}
          ageOffsetS={ageOffsetS}
          selectedHex={selectedHex}
          onSelect={setSelectedHex}
          showLabels={zoom >= LABEL_MIN_ZOOM && visible.length <= LABEL_MAX_VISIBLE}
        />
      </MapContainer>

      <div className="hud hud-top">
        <span className={`pill ${showSpinner ? "pill-busy" : connected ? "pill-live" : "pill-off"}`}>
          {showSpinner && <span className="spinner" aria-hidden />}
          {pendingFocus
            ? t.radar.finding(pendingFocus.label)
            : !subscription
              ? t.radar.connecting
              : loadingShown
                ? t.radar.loadingArea
                : connected
                  ? sizeFilter
                    ? t.radar.liveFiltered(visible.length, inView.length)
                    : t.radar.live(visible.length)
                  : t.radar.offline}
        </span>
        <div className="hud-right">
          <SizeFilterChip
            value={sizeFilter}
            onChange={(v) => {
              setSizeFilter(v);
              saveSizeFilter(v);
            }}
          />
          <SearchBox onPick={focusOn} />
        </div>
      </div>

      {/* One note slot, in priority order. These used to be separate elements at the same position,
          so two at once overlapped into an unreadable stack. */}
      {notice ? (
        <div className="hud hud-note">{notice}</div>
      ) : locationError ? (
        <div className="hud hud-note">{t.radar.locationDenied}</div>
      ) : allAsleep ? (
        <div className="hud hud-note-slot">
          {/* A real link: the .hud layer passes taps through to the map. */}
          <Link to="/alerts" className="hud-note hud-note-link">
            {earliestWake !== undefined ? t.radar.alertsAsleepUntil(formatWake(earliestWake)) : t.radar.alertsAsleep}
          </Link>
        </div>
      ) : null}

      {!selected && <AltitudeLegend units={units} />}

      <button className="locate" onClick={locate} aria-label={t.radar.locate} disabled={locating}>
        {locating ? "◌" : "◎"}
      </button>

      {/* Asked once, on first use, and only when we know the answer isn't already "no" (D4). */}
      {settings && !settings.home_prompt_dismissed && settings.home_lat === null && !selected && (
        <HomePrompt
          onUseLocation={() =>
            new Promise<void>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  patch({ home_lat: pos.coords.latitude, home_lon: pos.coords.longitude })
                    .then(() => resolve())
                    .catch(reject);
                },
                () => reject(new Error(t.homePrompt.failed)),
                { enableHighAccuracy: true, timeout: 15_000 },
              );
            })
          }
          onDismiss={() => void patch({ home_prompt_dismissed: true })}
        />
      )}

      {selected && (
        <DetailSheet
          aircraft={selected}
          detail={detail}
          photo={photo}
          route={route}
          outlook={outlook}
          projection={projection}
          projectionLoading={projectionLoading}
          devicePosition={devicePosition}
          homePosition={
            settings && settings.home_lat !== null && settings.home_lon !== null
              ? { lat: settings.home_lat, lon: settings.home_lon }
              : null
          }
          units={units}
          isMuted={selected.type ? muted.has(selected.type) : false}
          onToggleMute={(icaoType) => void toggleMute(icaoType)}
          onClose={() => setSelectedHex(null)}
        />
      )}
    </div>
  );
}
